import { useEffect } from "react";
import useFetch from "hooks/useFetch";
import mixpanel from "mixpanel-browser";
import { useSetRecoilState } from "recoil";
import {
  axiosInstance,
  clearAuthHeaders,
  type CustomResponseFormat,
} from "api/instance";
import {
  COACH_AUTH_MODE,
  markCoachBrowser,
  setCoachToken,
} from "api/coachAuth";
import { userAtom, type UserType } from "recoil/userAtom";
import { useNavigate } from "react-router-dom";

/**
 * 코치 로그인 (JWT 세션).
 * /user/v2/login → 액세스 토큰은 메모리(coachAuth)에, 리프레시 토큰은 서버가 HttpOnly 쿠키로 →
 * /user/detail 로 프로필을 받아 userAtom 에 저장한다. userAtom(localStorage)에는 토큰이 들어가지 않는다.
 */
export default function useLoginCoach({
  url,
  requestBody,
  onNotCoach,
}: {
  url: string;
  requestBody: string;
  onNotCoach?: () => void;
}) {
  let navigate = useNavigate();

  const [loginResult, login] = useFetch({
    url: url,
    requestBody: requestBody,
  });

  const setUser = useSetRecoilState(userAtom);

  useEffect(() => {
    if (!loginResult) return;
    const result = loginResult.result;
    if (!result?.accessToken) return;

    const email: string | undefined = result.userSimple?.email ?? result.email;
    if (email === undefined || !email.includes("@orwellhealth.org")) {
      if (onNotCoach) onNotCoach();
      return;
    }

    (async () => {
      // 레거시 헤더가 남아 있으면 서버가 그걸 먼저 보므로 먼저 비운다.
      clearAuthHeaders();
      setCoachToken(result.accessToken);
      try {
        const detail =
          await axiosInstance.get<CustomResponseFormat<UserType>>(
            "/user/detail"
          );
        const profile = detail?.result;
        if (!profile) throw new Error("no profile");
        // 토큰 필드는 절대 저장하지 않는다.
        const { accessToken: _omit, ...rest } = profile;
        setUser({ ...rest, authMode: COACH_AUTH_MODE });
        markCoachBrowser();
        if (rest.trackId) mixpanel.identify(rest.trackId);
        navigate("/", { replace: true });
      } catch {
        setCoachToken(null);
        if (onNotCoach) onNotCoach();
      }
    })();
  }, [loginResult]);

  return login;
}
